import { API_URLS } from '../../../routes/urls';
import { apiSlice } from '../../apiSlice';
import { StrOrNum } from '../../../types/util';
import { generatePath } from 'react-router-dom';
import { QueryErrorReturnValue } from '../../types';
import { addSnackbar } from '../snackbar/snackbarSlice';

interface UnreadInquiryCountResponse {
  success: boolean;
  count: number;
}
export type InquiryParticipantType = 'USER' | 'INSTITUTE';
export interface Inquiry {
  id: StrOrNum;
  senderId: StrOrNum;
  senderType: InquiryParticipantType;
  isRead: boolean;
  sentAt: string;
  message: string;
}

export interface InquiryParticipant {
  id: StrOrNum;
  name: string;
  pictureLink?: string;
  type: InquiryParticipantType;
}

export interface InquiryThread {
  id: StrOrNum;
  lastInquiry?: null | Inquiry;
  participant: InquiryParticipant;
  noOfUnreadInquiry: number;
}

interface InquiryThreadListResponse {
  success: boolean;
  threads: InquiryThread[];
}

interface ThreadInquiriesListResponse {
  success: boolean;
  nextCursor: string | null;
  inquiries: Inquiry[];
}

export interface SendInquiryResponse {
  success: boolean;
  message: string;
  inquiry: Inquiry;
  inquiryThread: InquiryThread;
}

interface SendInquiryPayload {
  receiverId: StrOrNum;
  message: string;
}

const inquiryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    unreadCount: builder.query<UnreadInquiryCountResponse, void>({
      query: () => ({
        url: API_URLS.unreadInquiryCount,
      }),
    }),

    inquiryThreadList: builder.query<InquiryThreadListResponse, void>({
      query: () => ({
        url: API_URLS.inquiryThreads,
      }),
      providesTags: [{ type: 'Threads' }],
    }),

    threadInquiries: builder.query<ThreadInquiriesListResponse, { cursor: string | null; threadId: StrOrNum }>({
      query: ({ cursor, threadId }) => ({
        url: generatePath(API_URLS.inquiryThreadMessages, { threadId: threadId.toString() }),
        params: { cursor },
      }),
      providesTags: (_res, _err, payload) => [{ type: 'Threads', id: payload.threadId.toString() }],
    }),

    sendInquiry: builder.mutation<SendInquiryResponse, SendInquiryPayload>({
      query: ({ message, receiverId }) => ({
        url: API_URLS.sendInquiry,
        data: {
          receiverType: 'INSTITUTE',
          receiverId,
          message,
        },
        method: 'POST',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          dispatch(
            addSnackbar({
              message: err.error.message,
              severity: 'error',
            })
          );
        }
      },
      invalidatesTags: ['Threads'],
    }),
  }),
});

export const { useUnreadCountQuery, useInquiryThreadListQuery, useLazyThreadInquiriesQuery, useSendInquiryMutation } =
  inquiryApi;
